import { useRef, useState } from "react";
import HNotification from "../components/custom/notification/HNotification";

const useAudioRecording = (shouldUploadAsAttachment = false) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isRecordingLoading, setIsRecordingLoading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);

  const handleRecordingError = (error) => {
    console.error("Error accessing microphone:", error);
    if (error.name === "NotAllowedError") {
      HNotification.notify({type: "error", message: "Microphone", description: "Please enable microphone access in your browser settings"});
    }
    setIsRecording(false);
    setIsRecordingLoading(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
  };

  const startRecording = async () => {
    try {
      setIsRecording(true);
      if (!mediaRecorderRef.current) {
        setIsRecordingLoading(true);
        const { default: MicRecorder } = await import("mic-recorder-to-mp3");
        mediaRecorderRef.current = new MicRecorder({ bitRate: 128 });
        setIsRecordingLoading(false);
      }
      setRecordingTime(0);
      await mediaRecorderRef.current.start().catch((error) => {
        handleRecordingError(error);
        return false;
      });
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      return true;
    } catch (error) {
      handleRecordingError(error);
      return false;
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      return new Promise(async (resolve) => {
        try {
          const [buffer, blob] = await mediaRecorderRef.current.stop().getMp3();
          mediaRecorderRef.current = null; // unload to free memory

          const file = new File(buffer, "recording.mp3", {
            type: "audio/mpeg",
            lastModified: Date.now(),
          });
          resolve(file);
          setIsRecording(false);
          setRecordingTime(0);
        } catch (e) {
          HNotification.notify({type: "error", message: "Recording failed", description: "Some unknown error occurred! Please try recording again."});
          console.error(e);
          resolve();
        } finally {
          if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
          }
          setIsRecordingLoading(false);
        }
      });
    }
    return null;
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return {
    isRecording,
    recordingTime,
    startRecording,
    stopRecording,
    formatRecordingTime,
    isRecordingLoading,
  };
};

export default useAudioRecording;
